import { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Bold, Italic, Heading, List, ListOrdered, Eye, Pencil } from "lucide-react";

function applyFormat(textarea, type) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;
  const selected = value.slice(start, end);

  let before = value.slice(0, start);
  let after = value.slice(end);
  let insert = selected;
  let newStart = start;
  let newEnd = end;

  const wrap = (marker) => {
    insert = `${marker}${selected || "text"}${marker}`;
    newStart = start + marker.length;
    newEnd = newStart + (selected ? selected.length : 4);
  };

  const linePrefix = (prefix, numbered = false) => {
    const lineStart = before.lastIndexOf("\n") + 1;
    const block = value.slice(lineStart, end);
    const lines = block.split("\n");
    const formatted = lines
      .map((ln, i) => (numbered ? `${i + 1}. ${ln}` : `${prefix}${ln}`))
      .join("\n");
    before = value.slice(0, lineStart);
    insert = formatted;
    after = value.slice(end);
    newStart = lineStart;
    newEnd = lineStart + formatted.length;
  };

  switch (type) {
    case "bold":
      wrap("**");
      break;
    case "italic":
      wrap("*");
      break;
    case "heading":
      linePrefix("### ");
      break;
    case "bullet":
      linePrefix("- ");
      break;
    case "numbered":
      linePrefix("", true);
      break;
    default:
      break;
  }

  const next = before + insert + after;
  return { next, newStart, newEnd };
}

export default function RichTextField({ label, value, editMode, onChange, placeholder, testId }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(false);

  const handleToolbar = (type) => {
    const ta = ref.current;
    if (!ta) return;
    const { next, newStart, newEnd } = applyFormat(ta, type);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newStart, newEnd);
    });
  };

  const tools = [
    { type: "bold", icon: Bold, label: "Bold" },
    { type: "italic", icon: Italic, label: "Italic" },
    { type: "heading", icon: Heading, label: "Heading" },
    { type: "bullet", icon: List, label: "Bullet list" },
    { type: "numbered", icon: ListOrdered, label: "Numbered list" },
  ];

  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      {editMode ? (
        <div className="richtext-editor" data-testid={`${testId}-editor`}>
          <div className="richtext-toolbar">
            {tools.map((t) => (
              <button
                type="button"
                key={t.type}
                className="rt-btn"
                title={t.label}
                onClick={() => handleToolbar(t.type)}
                data-testid={`${testId}-rt-${t.type}`}
              >
                <t.icon size={14} />
              </button>
            ))}
            <button
              type="button"
              className={`rt-btn rt-preview-toggle ${preview ? "rt-active" : ""}`}
              title={preview ? "Edit" : "Preview"}
              onClick={() => setPreview((p) => !p)}
              data-testid={`${testId}-rt-preview`}
            >
              {preview ? <Pencil size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {preview ? (
            <div className="richtext-preview" data-testid={`${testId}-preview`}>
              {value ? (
                <ReactMarkdown>{value}</ReactMarkdown>
              ) : (
                <span className="field-empty">Nothing to preview</span>
              )}
            </div>
          ) : (
            <textarea
              ref={ref}
              className="field-input field-textarea richtext-textarea"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              rows={6}
              data-testid={testId}
            />
          )}
        </div>
      ) : (
        <div className="field-value richtext-display" data-testid={`display-${testId}`}>
          {value ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <span className="field-empty">Not set</span>
          )}
        </div>
      )}
    </div>
  );
}
